import * as React from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { faAngleDown, faAngleUp } from "@performyard/fontawesome/pro-regular-svg-icons";

import { Icon } from "../../../atoms/Icon";

const ICON_SIZE = 20;
const CHEVRON_SIZE = 16;

export type SideNavGroupProps = {
	id: string;
	label: string;
	icon?: IconDefinition;
	/** @default false */
	defaultOpen?: boolean;
	children: React.ReactNode;
};

const triggerClassName = [
	"group/menu-item relative flex items-center transition-colors",
	"box-border shrink-0",
	"m-0 appearance-none border-0 bg-transparent no-underline [font-family:inherit]",
	"text-[color:var(--sidenav-fg,currentColor)]",
	"focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--sidenav-fg,currentColor)]",
	"h-10 rounded-md text-paragraph-3 font-semibold",
	"cursor-pointer hover:bg-[color:var(--sidenav-hover-bg,transparent)]",
	"mx-2 gap-3 px-2",
].join(" ");

export const SideNavGroup = ({ id, label, icon, defaultOpen = false, children }: SideNavGroupProps) => {
	const [open, setOpen] = React.useState(defaultOpen);

	React.useEffect(() => {
		if (defaultOpen) setOpen(true);
	}, [defaultOpen]);

	const regionId = `sidenav-group-region-${id}`;
	const triggerId = `sidenav-group-trigger-${id}`;

	return (
		<div data-group-id={id} className="flex w-full shrink-0 flex-col">
			<button
				id={triggerId}
				type="button"
				className={triggerClassName}
				onClick={() => setOpen((prev) => !prev)}
				aria-expanded={open}
				aria-controls={regionId}
				data-testid={`sidenav-group-${id}`}
			>
				<span aria-hidden className="flex size-5 shrink-0 items-center justify-center">
					{icon ? <Icon icon={icon} size={ICON_SIZE} color={null} /> : null}
				</span>
				<span className="min-w-0 flex-1 truncate text-left">{label}</span>
				<span aria-hidden className="flex size-4 shrink-0 items-center justify-center">
					<Icon icon={open ? faAngleUp : faAngleDown} size={CHEVRON_SIZE} color={null} />
				</span>
			</button>
			{open ? (
				<div id={regionId} role="region" aria-labelledby={triggerId} className="flex w-full flex-col">
					{children}
				</div>
			) : null}
		</div>
	);
};

SideNavGroup.displayName = "SideNavGroup";
